import { forwardRef } from "react";

import * as RadixDropdownMenu from "@radix-ui/react-dropdown-menu";

import { cn } from "../index";

/** --------------------------------------------------------------------------------------------------------------------
 * Dropdown
 * ------------------------------------------------------------------------------------------------------------------ */

type DropdownMenuProps = RadixDropdownMenu.DropdownMenuProps;

const Root = RadixDropdownMenu.Root;

Root.displayName = "DropdownMenu";

/** --------------------------------------------------------------------------------------------------------------------
 * Trigger
 * ------------------------------------------------------------------------------------------------------------------ */

type TriggerProps = RadixDropdownMenu.DropdownMenuTriggerProps;

const Trigger = RadixDropdownMenu.Trigger;

Trigger.displayName = "DropdownMenuTrigger";

/** --------------------------------------------------------------------------------------------------------------------
 * Content
 * ------------------------------------------------------------------------------------------------------------------ */

const contentClassName = `p-[10px] rounded-[10px] bg-primary-white shadow-[var(--shadow-elevated)] duration-[400ms] ease-[cubic-bezier(0.16,1,0.3,1)] will-change-[transform,opacity] data-[side=top]:animate-slideDownAndFade data-[side=right]:animate-slideLeftAndFade data-[side=bottom]:animate-slideUpAndFade data-[side=left]:animate-slideRightAndFade`;

type ContentProps = RadixDropdownMenu.DropdownMenuContentProps;

const Content = forwardRef<HTMLDivElement, ContentProps>(({ className, ...props }, forwardedRef) => (
  <RadixDropdownMenu.Portal>
    <RadixDropdownMenu.Content {...props} ref={forwardedRef} className={cn(contentClassName, className)} />
  </RadixDropdownMenu.Portal>
));

Content.displayName = "DropdownMenuContent";

/** --------------------------------------------------------------------------------------------------------------------
 * Arrow
 * ------------------------------------------------------------------------------------------------------------------ */

type ArrowProps = RadixDropdownMenu.DropdownMenuArrowProps;

const Arrow = forwardRef<SVGSVGElement, ArrowProps>(({ className, ...props }, forwardedRef) => (
  <RadixDropdownMenu.Arrow {...props} ref={forwardedRef} className={cn("fill-primary-white", className)} />
));

Arrow.displayName = "DropdownMenuArrow";

/** --------------------------------------------------------------------------------------------------------------------
 * Item
 * ------------------------------------------------------------------------------------------------------------------ */

const itemClassName =
  "flex relative items-center cursor-pointer outline-none rounded-[10px] pt-2.5 pr-[25px] pb-2.5 pl-[10px] text-primary-dark-blue text-base select-none data-disabled:pointer-events-none data-disabled:text-primary-dark-gray data-highlighted:bg-primary-gray [&>svg]:shrink-0 [&>svg]:mr-2.5 [&>svg]:w-4.5 [&>svg]:h-4.5 [&>svg]:text-primary-dark-gray";

type ItemProps = RadixDropdownMenu.DropdownMenuItemProps;

const Item = forwardRef<HTMLDivElement, ItemProps>(({ className, ...props }, forwardedRef) => (
  <RadixDropdownMenu.Item {...props} ref={forwardedRef} className={cn(itemClassName, className)} />
));

Item.displayName = "DropdownMenuItem";

/** --------------------------------------------------------------------------------------------------------------------
 * Group
 * ------------------------------------------------------------------------------------------------------------------ */

type GroupProps = RadixDropdownMenu.DropdownMenuGroupProps;

const Group = RadixDropdownMenu.Group;

Group.displayName = "DropdownMenuGroup";

/** --------------------------------------------------------------------------------------------------------------------
 * Label
 * ------------------------------------------------------------------------------------------------------------------ */

type LabelProps = RadixDropdownMenu.DropdownMenuLabelProps;

const Label = forwardRef<HTMLDivElement, LabelProps>(({ className, ...props }, forwardedRef) => (
  <RadixDropdownMenu.Label
    {...props}
    ref={forwardedRef}
    className={cn(itemClassName, "px-1.25 py-2.5 font-semibold", className)}
  />
));

Label.displayName = "DropdownMenuLabel";

/** --------------------------------------------------------------------------------------------------------------------
 * CheckboxItem
 * ------------------------------------------------------------------------------------------------------------------ */

type CheckboxItemProps = RadixDropdownMenu.DropdownMenuCheckboxItemProps;

const CheckboxItem = forwardRef<HTMLDivElement, CheckboxItemProps>(({ className, ...props }, forwardedRef) => (
  <RadixDropdownMenu.CheckboxItem {...props} ref={forwardedRef} className={cn(itemClassName, className)} />
));

CheckboxItem.displayName = "DropdownMenuCheckboxItem";

/** --------------------------------------------------------------------------------------------------------------------
 * RadioGroup
 * ------------------------------------------------------------------------------------------------------------------ */

type RadioGroupProps = RadixDropdownMenu.DropdownMenuRadioGroupProps;

const RadioGroup = RadixDropdownMenu.RadioGroup;

RadioGroup.displayName = "DropdownMenuRadioGroup";

/** --------------------------------------------------------------------------------------------------------------------
 * RadioItem
 * ------------------------------------------------------------------------------------------------------------------ */

type RadioItemProps = RadixDropdownMenu.DropdownMenuRadioItemProps;

const RadioItem = forwardRef<HTMLDivElement, RadioItemProps>(({ className, ...props }, forwardedRef) => (
  <RadixDropdownMenu.RadioItem {...props} ref={forwardedRef} className={cn(itemClassName, "radio-item", className)} />
));

RadioItem.displayName = "DropdownMenuRadioItem";

/** --------------------------------------------------------------------------------------------------------------------
 * ItemIndicator
 * ------------------------------------------------------------------------------------------------------------------ */

type ItemIndicatorProps = RadixDropdownMenu.DropdownMenuItemIndicatorProps;

const ItemIndicator = forwardRef<HTMLSpanElement, ItemIndicatorProps>(({ className, ...props }, forwardedRef) => (
  <RadixDropdownMenu.ItemIndicator
    {...props}
    ref={forwardedRef}
    className={cn(
      `right-0 w-6.25 absolute inline-flex items-center justify-center [.radio-item_&]:static [.radio-item_&]:w-auto`,
      className,
    )}
  />
));

ItemIndicator.displayName = "DropdownMenuItemIndicator";

/** --------------------------------------------------------------------------------------------------------------------
 * Sub
 * ------------------------------------------------------------------------------------------------------------------ */

type SubProps = RadixDropdownMenu.DropdownMenuSubProps;

const Sub = RadixDropdownMenu.Sub;

Sub.displayName = "DropdownMenuSub";

/** --------------------------------------------------------------------------------------------------------------------
 * SubTrigger
 * ------------------------------------------------------------------------------------------------------------------ */

type SubTriggerProps = RadixDropdownMenu.DropdownMenuSubTriggerProps;

const SubTrigger = forwardRef<HTMLDivElement, SubTriggerProps>(({ className, ...props }, forwardedRef) => (
  <RadixDropdownMenu.SubTrigger
    {...props}
    ref={forwardedRef}
    className={cn(itemClassName, "data-[state='open']:font-semibold", className)}
  />
));

SubTrigger.displayName = "DropdownMenuSubTrigger";

/** --------------------------------------------------------------------------------------------------------------------
 * SubContent
 * ------------------------------------------------------------------------------------------------------------------ */

type SubContentProps = RadixDropdownMenu.DropdownMenuSubContentProps;

const SubContent = forwardRef<HTMLDivElement, SubContentProps>(({ className, ...props }, forwardedRef) => (
  <RadixDropdownMenu.SubContent
    {...props}
    ref={forwardedRef}
    className={cn(contentClassName, className)}
    sideOffset={12}
    alignOffset={-10}
  />
));

SubContent.displayName = "DropdownMenuSubContent";

export {
  Arrow,
  CheckboxItem,
  Content,
  Group,
  Item,
  ItemIndicator,
  Label,
  RadioGroup,
  RadioItem,
  Root,
  Sub,
  SubContent,
  SubTrigger,
  Trigger,
};
export type {
  ArrowProps,
  CheckboxItemProps,
  ContentProps,
  DropdownMenuProps,
  GroupProps,
  ItemIndicatorProps,
  ItemProps,
  LabelProps,
  RadioGroupProps,
  RadioItemProps,
  SubContentProps,
  SubProps,
  SubTriggerProps,
  TriggerProps,
};
